import React, { useCallback, useState } from 'react';

import { Button } from 'src/common/button';
import { useSharePanelStore } from 'src/common/store/share.store';
import { uploadImageAssetToS3 } from 'src/common/helpers/file-upload';
import { functionNoop } from 'src/utils/function/noop';

interface Props {
  onThumbnailUrlUpdate: (url: string) => void;
  onChangeUploadedFileClicked: () => void;
}

export const ShareThumbnailConfirmationOverlay = ({ onThumbnailUrlUpdate, onChangeUploadedFileClicked }: Props) => {
  const { selectedImageUrl, resetThumbnailEditor, uploadedFile } = useSharePanelStore((state) => ({
    selectedImageUrl: state.selectedImageUrl,
    resetThumbnailEditor: state.resetThumbnailEditor,
    uploadedFile: state.uploadedThumbnailFile,
  }));
  const [uploading, setUploading] = useState(false);

  const onConfirmFile = useCallback(async () => {
    if (uploadedFile) {
      setUploading(true);
      try {
        const uploadedS3Asset = await uploadImageAssetToS3(uploadedFile, functionNoop);
        setUploading(false);
        onThumbnailUrlUpdate(uploadedS3Asset.url);
      } catch (err) {
        if ((err as any).code !== 'RequestAbortedError') {
          throw err;
        }
      }
    } else if (selectedImageUrl) {
      onThumbnailUrlUpdate(selectedImageUrl);
    }
    resetThumbnailEditor();
  }, [uploadedFile, selectedImageUrl, resetThumbnailEditor, onThumbnailUrlUpdate]);

  if (!uploadedFile && !selectedImageUrl) return null;

  return (
    <div className='absolute inset-0 rounded-lg block bg-bedrock-black/backdrop'>
      <div className='flex space-x-md w-full h-full items-end justify-start p-md'>
        <div className='flex justify-start space-x-md'>
          <Button
            size='regular'
            variant='secondary'
            label={uploadedFile ? 'Use image' : 'Use thumbnail'}
            onClick={onConfirmFile}
            disabled={uploading}
          />
          <Button
            size='regular'
            variant='tertiary'
            label='Cancel'
            onClick={resetThumbnailEditor}
            className='text-bedrock-white'
            disabled={uploading}
          />
          {uploadedFile && (
            <Button
              size='regular'
              variant='tertiary'
              label='Change'
              onClick={onChangeUploadedFileClicked}
              className='text-bedrock-white'
              disabled={uploading}
            />
          )}
        </div>
      </div>
    </div>
  );
};
